import { useId, useRef, useState } from "react";
import { Form, useActionData, useNavigation } from "react-router";
import type { Me, MeUser } from "@repo/shared-types";
import { Avatar } from "@/components/avatar";
import { PictureFrame } from "@/components/picture-frame";
import { ErrorNote, PictureButton } from "@/components/form";
import { Button } from "@/components/ui/button";
import { Field, FieldDescription, FieldError, FieldLabel } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { AccountPage, type ActionResult } from "@/routes/account-section";
import { useSession } from "@/routes/root";

/** Profile, opened from you at the top of Account: your picture, your name (0076) and your username (ADR 0068). */

export default function AccountProfile() {
  const session = useSession();
  // The loader let the page draw only with a session.
  return session && <Profile session={session} />;
}

function Profile({ session }: { session: Me }) {
  const result = useActionData() as ActionResult | undefined;
  const busy = useNavigation().state === "submitting";
  const about = (intent: string) => (result?.intent === intent ? result : undefined);

  return (
    <AccountPage title="Profile">
      {/* Keyed by the picture, so a picture saved or removed starts the box afresh. */}
      {session.images && (
        <ProfilePicture key={session.user.image ?? ""} user={session.user} busy={busy} error={about("avatar")?.error} />
      )}
      <Name name={session.user.name ?? ""} busy={busy} error={about("name")?.error} />
      <Username username={session.user.username} busy={busy} error={about("username")?.error} />
    </AccountPage>
  );
}

function Name({ name, busy, error }: { name: string; busy: boolean; error?: string }) {
  const id = useId();
  return (
    <Form method="post">
      <input type="hidden" name="intent" value="name" />
      <Field data-invalid={error ? true : undefined}>
        <FieldLabel htmlFor={id}>Name</FieldLabel>
        <div className="flex gap-2">
          <Input
            id={id}
            name="name"
            defaultValue={name}
            maxLength={100}
            autoComplete="name"
            aria-invalid={error ? true : undefined}
            className="h-10"
          />
          <Button type="submit" variant="outline" size="lg" disabled={busy}>
            Save
          </Button>
        </div>
        {error && <FieldError>{error}</FieldError>}
        <FieldDescription>What boards and conversations call you. Without one, they show your username.</FieldDescription>
      </Field>
    </Form>
  );
}

function Username({ username, busy, error }: { username: string; busy: boolean; error?: string }) {
  const id = useId();
  return (
    <Form method="post">
      <input type="hidden" name="intent" value="username" />
      <Field data-invalid={error ? true : undefined}>
        <FieldLabel htmlFor={id}>Username</FieldLabel>
        <div className="flex gap-2">
          <Input
            id={id}
            name="username"
            defaultValue={username}
            pattern="[a-z0-9_\-]{3,30}"
            autoComplete="nickname"
            aria-invalid={error ? true : undefined}
            className="h-10"
          />
          <Button type="submit" variant="outline" size="lg" disabled={busy}>
            Save
          </Button>
        </div>
        {error && <FieldError>{error}</FieldError>}
        <FieldDescription>3 to 30 characters of a–z, 0–9, hyphen or underscore.</FieldDescription>
      </Field>
    </Form>
  );
}

/**
 * Your profile picture: shown beside your name wherever you are named, or
 * your initials until you choose one. A picture chosen is framed in a square,
 * by dragging it, before it is saved.
 */
function ProfilePicture({ user, busy, error }: { user: MeUser; busy: boolean; error?: string }) {
  const input = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-6">
        <Form method="post" encType="multipart/form-data" className="flex flex-wrap items-center gap-6">
          <input type="hidden" name="intent" value="avatar" />
          {file ? (
            <div className="w-48">
              <PictureFrame file={file} aspect="1:1" round />
            </div>
          ) : (
            <Avatar person={user} size="lg" alt={user.image ? "Your profile picture" : "Your initials"} />
          )}
          <div className="flex flex-wrap items-center gap-2">
            <PictureButton
              name="file"
              size="lg"
              disabled={busy}
              inputRef={input}
              onChange={(event) => setFile(event.currentTarget.files?.[0] ?? null)}
            >
              {file ? "Choose another" : user.image ? "Replace picture" : "Choose a picture"}
            </PictureButton>
            {file && (
              <>
                <Button type="submit" size="lg" disabled={busy}>
                  {busy ? "Saving…" : "Save picture"}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="lg"
                  disabled={busy}
                  onClick={() => {
                    if (input.current) input.current.value = "";
                    setFile(null);
                  }}
                >
                  Cancel
                </Button>
              </>
            )}
          </div>
        </Form>
        {!file && user.image && (
          <Form method="post">
            <input type="hidden" name="intent" value="remove-avatar" />
            <Button type="submit" variant="outline" size="lg" disabled={busy}>
              Remove picture
            </Button>
          </Form>
        )}
      </div>
      {file && <p className="text-sm text-muted-foreground">Drag it to frame it; zoom with the slider.</p>}
      {error && <ErrorNote message={error} />}
    </div>
  );
}
